
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowUpRight, ArrowDownRight, Coins } from 'lucide-react';
import { useWalletRepository } from '@/hooks/useRepositoryFactory';
import { Transaction } from '@/shared/types/entities';

interface TransactionHistoryProps {
  limit?: number;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({ 
  limit = 5
}) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const walletRepository = useWalletRepository();

  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoading(true);
      try {
        const data = await walletRepository.getCurrentUserRecentTransactions(limit);
        setTransactions(data);
      } catch (error) {
        console.error('Erro ao carregar histórico:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTransactions();
  }, [limit]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico de Transações</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
        </CardContent>
      </Card>
    );
  }

  if (transactions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico de Transações</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground text-sm py-4">
            Nenhuma transação encontrada
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <Coins className="h-5 w-5 mr-2 text-brand-blue" />
          Histórico de Transações
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {transactions.map(transaction => (
          <div key={transaction.id} className="flex justify-between items-center pb-2 border-b">
            <div className="flex items-start gap-2">
              {transaction.type === 'credit' ? (
                <ArrowUpRight className="h-4 w-4 mt-1 text-green-500" />
              ) : (
                <ArrowDownRight className="h-4 w-4 mt-1 text-red-500" />
              )}
              <div>
                <p className="text-sm font-medium">{transaction.description || 'Transação'}</p>
                <p className="text-xs text-muted-foreground">{formatDate(transaction.created_at)}</p>
              </div>
            </div>
            <Badge variant={transaction.type === 'credit' ? 'outline' : 'destructive'} className="ml-auto">
              {transaction.type === 'credit' ? '+' : '-'}{transaction.amount} Seshs
            </Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
